'use client';

import { useRef } from 'react';
import { routePath } from '@/lib/routes';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER } from './bits';
import DossierHead from './DossierHead';
import { useChoreo } from './motion';

/**
 * §09 — the compare hub. Same format, same sources discipline, same
 * concessions section — different competitor. Unbuilt siblings render as
 * non-clickable "IN WRITING" cards (owner call: honest, and each flips live
 * via its data flag the moment the page ships).
 */
export default function CompareHub({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);

  useChoreo(rootRef, (root) => {
    return gsap.from(root.querySelectorAll('[data-hub-card]'), {
      y: 14,
      opacity: 0,
      duration: 0.5,
      ease: EASE_OUT,
      stagger: 0.07,
      paused: true,
    });
  });

  const cardClass =
    'inline-flex items-center gap-3 rounded-ref-sm border border-line-2 bg-surface px-5 py-3.5 text-ink';

  return (
    <section ref={rootRef} className="pt-16 md:pt-24">
      <div className={CONTAINER}>
        <DossierHead
          num="09"
          label="KEEP COMPARING"
          title="This dossier has siblings."
          titleClassName="text-[clamp(24px,2.6vw,30px)] leading-[1.15]"
          lead="Same format, same sources discipline, same concessions section — different competitor."
          leadClassName="max-w-[540px]"
        />
        <div className="mt-7 flex flex-wrap gap-3">
          {data.hub.map((entry) =>
            entry.live ? (
              <a
                key={entry.name}
                data-hub-card
                href={routePath(entry.route)}
                className={`${cardClass} transition-colors duration-200 hover:border-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600`}
              >
                <span className="text-[14px] font-semibold">rii.link vs {entry.name}</span>
                <span className="font-jetbrains text-[10.5px] text-dim">{entry.path}</span>
                <span aria-hidden="true" className="text-brand-600">
                  &rarr;
                </span>
              </a>
            ) : (
              <span key={entry.name} data-hub-card className={cardClass}>
                <span className="text-[14px] font-semibold">rii.link vs {entry.name}</span>
                <span className="font-jetbrains text-[10.5px] text-dim">{entry.path}</span>
                <span className="rounded-[4px] border border-line-3 px-1.5 py-px font-jetbrains text-[9px] tracking-[0.08em] text-dim">
                  IN WRITING
                </span>
              </span>
            ),
          )}
        </div>
      </div>
    </section>
  );
}
